import React, { useState } from "react";
import { Box, Checkbox, Typography } from "@mui/material";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import { useTranslation } from "react-i18next";
import color from "../../../Constants/colors";
import { useWorkspaceColors } from "../../../Contexts/WorkspaceColorContext";
import { VoicesPost } from "../../../Models/voicesPosts";
import { useDashboardColors } from "../../HomePage/dashboardColors";
import { VoicesStatusTag } from "../Common/VoicesFilterParts";
import VoicesPostDialog from "./VoicesPostDialog";
import VoicesPostStateChip from "./VoicesPostStateChip";

interface Props {
  posts: VoicesPost[];
  emptyText: string;
  showCampaign?: boolean;
  selectable?: (post: VoicesPost) => boolean;
  selectedIds?: number[];
  onToggle?: (postId: number) => void;
}

export const formatPostTime = (value: string) =>
  new Date(value).toLocaleString([], { dateStyle: "short", timeStyle: "short" });

const initials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");

const VoicesPostRows: React.FC<Props> = ({
  posts,
  emptyText,
  showCampaign = false,
  selectable,
  selectedIds = [],
  onToggle,
}) => {
  const { t } = useTranslation("voicesPosts");
  const { colors: workspace } = useWorkspaceColors();
  const { accentSoft, accentIcon } = useDashboardColors();
  const [openPostId, setOpenPostId] = useState<number | null>(null);

  const openPost = posts.find((post) => post.id === openPostId) ?? null;

  const details = (post: VoicesPost) =>
    [post.scheduled_at && formatPostTime(post.scheduled_at), showCampaign && post.campaign_name]
      .filter(Boolean)
      .join(" · ");

  const renderRow = (post: VoicesPost) => (
    <Box component="li" key={post.id}>
      <Box
        role="button"
        tabIndex={0}
        onClick={() => setOpenPostId(post.id)}
        onKeyDown={(event) => event.key === "Enter" && setOpenPostId(post.id)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 1,
          py: 1,
          borderRadius: "10px",
          cursor: "pointer",
          "&:hover": { backgroundColor: accentSoft },
          "&:focus-visible": {
            outline: `2px solid ${workspace.primary_dark}`,
            outlineOffset: -2,
          },
        }}
      >
        {selectable && (
          <Checkbox
            size="small"
            disabled={!selectable(post)}
            checked={selectedIds.includes(post.id)}
            onClick={(event) => event.stopPropagation()}
            onChange={() => onToggle?.(post.id)}
            sx={{
              p: 0.25,
              visibility: selectable(post) ? "visible" : "hidden",
              "&.Mui-checked": { color: workspace.accent },
            }}
          />
        )}
        <Box
          aria-hidden
          sx={{
            width: 36,
            height: 36,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "10px",
            fontSize: 12,
            fontWeight: 700,
            color: accentIcon,
            backgroundColor: accentSoft,
          }}
        >
          {post.target.kind === "company_page" ? (
            <BusinessOutlinedIcon sx={{ fontSize: 18 }} />
          ) : (
            initials(post.target.name)
          )}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, minWidth: 0 }}>
            <Typography
              noWrap
              sx={{
                fontSize: 14,
                lineHeight: "20px",
                fontWeight: 600,
                color: color.gray900,
                flexShrink: 0,
                maxWidth: "50%",
              }}
            >
              {post.target.name}
            </Typography>
            <Typography noWrap sx={{ fontSize: 12, lineHeight: "16px", color: color.gray500 }}>
              {details(post)}
            </Typography>
          </Box>
          <Typography noWrap sx={{ fontSize: 13, lineHeight: "18px", color: color.gray600 }}>
            {post.text.trim() || t("emptyText")}
          </Typography>
        </Box>
        {post.actions.includes("approve") && (
          <VoicesStatusTag label={t("yourApproval")} tone={color.orange600} />
        )}
        <VoicesPostStateChip post={post} />
      </Box>
    </Box>
  );

  return (
    <>
      {!posts.length ? (
        <Typography sx={{ fontSize: 13, color: color.gray500 }}>{emptyText}</Typography>
      ) : (
        <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
          {posts.map(renderRow)}
        </Box>
      )}

      <VoicesPostDialog
        open={!!openPost}
        campaignId={openPost?.campaign_id ?? null}
        post={openPost}
        topic={null}
        onClose={() => setOpenPostId(null)}
      />
    </>
  );
};

export default VoicesPostRows;
